import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../services/CalibrationService";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Autocomplete from "@mui/material/Autocomplete";
import Tooltip from "@mui/material/Tooltip";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Histogram from "../Histogram";
import AspenMap from "./AspenMap";
import { normalizeRootGeoJSON, normalizeClientAssetData } from "../ClientAssetDataMap";
import {
    COMPASS_GRID,
    normalizeCardinalDirections,
    getSpanKeyDirections,
    defaultAllowedMastHeadings,
} from "./cardinalDirectionUtils";
import { EXCELLENT_TIER_THRESHOLD, GOOD_TIER_THRESHOLD, FAIR_TIER_THRESHOLD, getCellCommsColor } from "./InstallationDetails";

const SENSOR_COLOR_ACCEPTED = "#17a773";
const SENSOR_COLOR_DEFAULT = "#b56aff";
const FEET_PER_METER = 3.28084;
const PCT_TARGET_P10 = 0.10;
const PCT_TARGET_P10_20 = 0.10;
const PCT_TARGET_P50 = 0.80;
const PCT_LOW_THRESHOLD_P50 = 0.50;

const TOWER_TYPES = [
    "Lattice",
    "H-Frame",
    "Wood Monopole",
    "Steel Monopole",
];

const TOWER_TYPES_WITHOUT_LEGS = ["H-Frame", "Wood Monopole", "Steel Monopole"];
const ASSET_DATA_TYPES = ["RootGeojson", "ClientAssetData"]; //allows back comptability



const useStyles = makeStyles(() => ({
    contentContainer: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        gap: "30px",
        maxHeight: "calc(100vh - 57px)",
    },
    sidebar: {
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "4px 2px",
        flexGrow: 1,
    },
    noSensorSelected: {
        padding: "16px 12px",
        color: "#888888",
        fontStyle: "italic",
        fontSize: "13px",
    },
    sensorBoxExpanded: {
        border: "1px solid #1976d2",
        borderRadius: "6px",
        padding: "10px 12px",
        backgroundColor: "#e8f0fb",
    },
    sensorName: {
        fontWeight: "bold",
        fontSize: "13px",
        marginBottom: "2px",
    },
    sensorDetail: {
        fontSize: "12px",
        color: "#555555",
        marginBottom: "8px",
    },
    metricsPanel: {
        border: "1px solid #dddddd",
        borderRadius: "6px",
        padding: "8px 12px",
        backgroundColor: "#f9f9f9",
        marginBottom: "8px",
        fontSize: "14px",
    },
    metricsRow: {
        padding: "2px 0",
    },
    metricsLabel: {
        color: "#666666",
    },
    metricsValue: {
        fontWeight: "bold",
    },
}));

const WSPD_RATING_COLORS = {
    Good: "#2e7d32",
    Fair: "#f57f17",
    Low: "#c62828",
};

function computeCoverageMetrics(placementData) {
    const pdMetrics = placementData?.data?.metrics ?? {};
    const displayMetrics = [];
    if (pdMetrics.num_spans != null && Array.isArray(pdMetrics.uncovered_span_ids)) {
        const uncoveredSpans = pdMetrics.uncovered_span_ids.length;
        displayMetrics.push({
            key: "span_coverage",
            label: "Span Coverage",
            value: `${pdMetrics.num_spans - uncoveredSpans} / ${pdMetrics.num_spans} covered`,
            valueColor: uncoveredSpans === 0 ? "#2e7d32" : "#c62828",
            tooltip: "All spans must be within 20km of at least 2 sensors",
        });
    }
    if (Array.isArray(pdMetrics.required_line_coverage) && Array.isArray(pdMetrics.uncovered_line_sets)) {
        const uncoveredLines = pdMetrics.uncovered_line_sets.length;
        const subRows = [];
        if (Array.isArray(pdMetrics.uncoverable_lines) && pdMetrics.uncoverable_lines.length > 0) {
            subRows.push({
                key: "uncoverable_lines",
                label: "Uncoverable Lines",
                value: `${pdMetrics.uncoverable_lines.length}${pdMetrics.num_lines != null ? ` / ${pdMetrics.num_lines}` : ""}`,
                valueColor: "#c62828",
                tooltipHeader: "Lines have no accessible structures.",
                tooltipItems: pdMetrics.uncoverable_lines,
            });
        }
        subRows.push({
            key: "coverable_lines",
            label: "Coverable Lines",
            value: `${pdMetrics.required_line_coverage.length - uncoveredLines} / ${pdMetrics.required_line_coverage.length} covered`,
            valueColor: uncoveredLines === 0 ? "#2e7d32" : "#c62828",
            tooltipHeader: "All lines must have at least 1 sensor.",
            tooltipItems: uncoveredLines > 0 ? pdMetrics.uncovered_line_sets : [],
        });
        displayMetrics.push({
            key: "line_coverage",
            label: "Line Coverage",
            subRows,
        });
    }
    if (pdMetrics.num_sensors != null) {
        if (pdMetrics.num_sensors > 0) {
            const numSensors = pdMetrics.num_sensors;
            const p10Count = pdMetrics.num_sensors_below_10th_percentile_wspd ?? 0;
            const p10_20Count = pdMetrics.num_sensors_btwn_10th_20th_percentile_wspd ?? 0;
            const p50Count = pdMetrics.num_sensors_below_median_span_wspd ?? 0;
            const p10Pct = p10Count / numSensors;
            const p10_20Pct = p10_20Count / numSensors;
            const p50Pct = p50Count / numSensors;
            const classifyBucket = (pct, target, isP50) => {
                if (pct >= target) return "Good";
                if (isP50 ? pct < PCT_LOW_THRESHOLD_P50 : pct === 0) return "Low";
                return "Fair";
            };
            displayMetrics.push({
                key: "low_wspd_coverage",
                label: "Low Wspd Coverage",
                summary: `Out of ${numSensors} total sensors:`,
                infoTooltip: "If possible, sensors should be concentrated in low wspd locations.\nIdeally, sensors will capture spans with wspds below the 10th and 20th percentile of all span wspds, and most sensors will be in locations with wspds below the median (50th percentile).",
                subRows: [
                    {
                        key: "wspd_p10",
                        label: "<10th %-ile wspds",
                        badge: {
                            rating: classifyBucket(p10Pct, PCT_TARGET_P10, false),
                            count: p10Count,
                            totalCount: numSensors,
                            percentage: p10Pct,
                            tooltip: `Good: >=${PCT_TARGET_P10 * 100}% of sensors.\nLow: 0% of sensors`,
                        },
                    },
                    {
                        key: "wspd_p10_20",
                        label: "10-20th %-ile wspds",
                        badge: {
                            rating: classifyBucket(p10_20Pct, PCT_TARGET_P10_20, false),
                            count: p10_20Count,
                            totalCount: numSensors,
                            percentage: p10_20Pct,
                            tooltip: `Good: >=${PCT_TARGET_P10_20 * 100}% of sensors.\nLow: 0% of sensors`,
                        },
                    },
                    {
                        key: "wspd_p50",
                        label: "<50th %-ile wspds",
                        badge: {
                            rating: classifyBucket(p50Pct, PCT_TARGET_P50, true),
                            count: p50Count,
                            totalCount: numSensors,
                            percentage: p50Pct,
                            tooltip: `Good: >=${PCT_TARGET_P50 * 100}% of sensors.\nLow: <${PCT_LOW_THRESHOLD_P50 * 100}% of sensors`,
                        },
                    },
                ],
            });
        } else {
            displayMetrics.push({
                key: "low_wspd_coverage",
                label: "Low Wspd Coverage",
                value: "N/A",
                tooltip: "No low wspd coverage metrics found",
            });
        }
    }
    return displayMetrics;
}

function computePercentile(sorted, p) {
    if (p < 0 || p > 1) throw new RangeError(`computePercentile: p must be between 0 and 1, got ${p}`);
    if (sorted.length === 0) return null;
    const idx = p * (sorted.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    return lo === hi ? sorted[lo] : sorted[lo] + (idx - lo) * (sorted[hi] - sorted[lo]);
}

function avgWindSpeed(v) {
    if (v == null) return null;
    if (Array.isArray(v)) return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
    if (typeof v === "object") {
        const vals = Object.values(v);
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }
    return v;
}

function computeWindSpeedMetrics(placementData, inputData) {
    const windSpeedsByStructure = inputData?.data?.adjacent_span_wind_speeds_by_structure ?? {};
    const sensorWindSpeeds = Object.values(placementData?.data?.sensor_name_structure ?? {})
        .map((structureName) => avgWindSpeed(windSpeedsByStructure[structureName]))
        .filter((v) => v != null);

    const spanWindSpeedMap = {};
    for (const spans of Object.values(windSpeedsByStructure)) {
        if (typeof spans === "object" && !Array.isArray(spans) && spans !== null) {
            Object.assign(spanWindSpeedMap, spans);
        }
    }
    const spanWindSpeeds = Object.values(spanWindSpeedMap);
    const displayMetrics = [];
    if (spanWindSpeeds.length > 0) {
        const sorted = [...spanWindSpeeds].sort((a, b) => a - b);
        const pctileVlines = [0.10, 0.20, 0.50]
            .map((p) => computePercentile(sorted, p))
            .filter((v) => v != null)
            .map((v) => ({ value: v, dashed: true, color: "#666666" }));
        displayMetrics.push({
            key: "wind_speeds",
            metric_type: "histogram",
            value: {
                values: spanWindSpeeds,
                x_label: "m/s",
                y_label: "# spans",
                vlines: [...sensorWindSpeeds, ...pctileVlines],
                vline_legend: [
                    { type: "bar", color: "#1976d2", label: "All span wind speeds" },
                    { color: "#FF4560", dashed: false, label: "Wind speed at sensor locations" },
                    { color: "#666666", dashed: true, label: "10th / 20th / 50th percentile span wind speeds" },
                ],
            },
        });
    }
    return displayMetrics;
}

export default function SensorRefinement({ client, project }) {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [rootGeoJSON, setRootGeoJSON] = useState(null);
    const [clientAssetData, setClientAssetData] = useState(null);
    const [sensorPlacementData, setSensorPlacementData] = useState(null);
    const [sensorPlacementInputData, setSensorPlacementInputData] = useState(null);
    const [metricsExpanded, setMetricsExpanded] = useState(false);
    const [expandedSensorName, setExpandedSensorName] = useState(null);
    const [pendingInstallOptionIdx, setPendingInstallOptionIdx] = useState(0);
    const [pendingInstallHag, setPendingInstallHag] = useState("");
    const [hagUnit, setHagUnit] = useState("m");
    const [hagWarningDialog, setHagWarningDialog] = useState(false);
    const [pendingStructureName, setPendingStructureName] = useState("");
    const [pendingTowerType, setPendingTowerType] = useState(null);
    const [changingStructure, setChangingStructure] = useState(false);
    const [pendingAllowedMastHeadings, setPendingAllowedMastHeadings] = useState(new Set());
    const [pendingMountingTowerLeg, setPendingMountingTowerLeg] = useState(null);
    const [pendingHiltiInstallOverride, setPendingHiltiInstallOverride] = useState(false);
    const mapRef = useRef();
    const sidebarRef = useRef();
    const [ineligibleStructureFromUser, setIneligibleStructureFromUser] = useState(new Set());
    const [coverageMetrics, setCoverageMetrics] = useState([]);
    const [windSpeedMetrics, setWindSpeedMetrics] = useState([]);
    const [coverageExpanded, setCoverageExpanded] = useState(true);
    const [coverageView, setCoverageView] = useState("hide");
    const [wspdHighlightView, setWspdHighlightView] = useState(null);
    const [showUncoverableLines, setShowUncoverableLines] = useState(false);
    const [showUncoveredCoverableLines, setShowUncoveredCoverableLines] = useState(false);
    const showUncoveredSpans = coverageView === "spans";

    const loadSensorPlacementInputData = useCallback(async (sensorPlacementDataset) => {
        if (!sensorPlacementDataset) {
            setSensorPlacementInputData(null);
            return null;
        }
        const inputDatasetMeta = sensorPlacementDataset?.meta?.input_datasets?.find(
            (ds) => ds.dataset_type === "SensorPlacementInput"
        );
        if (inputDatasetMeta) {
            try {
                const inputData = await calibrationService.getExactDataset(
                    inputDatasetMeta.client_name,
                    inputDatasetMeta.project_name,
                    inputDatasetMeta.group_key,
                    "sensor-placement-input",
                    inputDatasetMeta.version_dttm.replace(/\.\d+([+-]\d{2}:\d{2}|Z)$/, "Z")
                );
                setSensorPlacementInputData(inputData ?? null);
                return inputData ?? null;
            } catch (err) {
                console.error("Failed to load sensor placement input dataset:", err);
                setSensorPlacementInputData(null);
                return null;
            }
        }
        setSensorPlacementInputData(null);
        return null;
    }, []);

    const reloadSensorPlacementData = useCallback(() => {
        return calibrationService
            .getLatestDatasetViaSignedUrl(client, project, DEFAULT_GROUP_KEY, "sensor-placement-options", false)
            .then((placementData) => {
                setSensorPlacementData(placementData ?? null);
                setIneligibleStructureFromUser(new Set());
                return placementData ?? null;
            });
    }, [client, project]);

    useEffect(() => {
        setCoverageMetrics(computeCoverageMetrics(sensorPlacementData));
        setWindSpeedMetrics(computeWindSpeedMetrics(sensorPlacementData, sensorPlacementInputData));
    }, [sensorPlacementData, sensorPlacementInputData]);

    useEffect(() => {
        if (!client || !project) {
            setRootGeoJSON(null);
            setClientAssetData(null);
            setSensorPlacementData(null);
            setSensorPlacementInputData(null);
            setCoverageMetrics([]);
            setWindSpeedMetrics([]);
            return;
        }
        setLoading(true);

        // The asset data (root-geojson or, for projects not yet migrated,
        // client-asset-data) is loaded separately from the input dataset referenced in the sensor placement dataset.
        const loadAssetData = async (sensorPlacementInputDataset) => {
            const assetDataMeta = sensorPlacementInputDataset?.meta?.input_datasets?.find((ds) =>
                ASSET_DATA_TYPES.includes(ds.dataset_type)
            );
            if (!assetDataMeta) return { rootGeoJSON: null, clientAssetData: null };

            const dttm = assetDataMeta.version_dttm.replace(/\.\d+([+-]\d{2}:\d{2}|Z)$/, "Z");
            try {
                if (assetDataMeta.dataset_type === "RootGeojson") {
                    const geoJson = await calibrationService.getExactDataset(
                        assetDataMeta.client_name,
                        assetDataMeta.project_name,
                        assetDataMeta.group_key,
                        "root-geojson",
                        dttm,
                        true
                    );
                    return { rootGeoJSON: geoJson ?? null, clientAssetData: null };
                }
                const legacyData = await calibrationService.getExactDataset(
                    assetDataMeta.client_name,
                    assetDataMeta.project_name,
                    assetDataMeta.group_key,
                    "client-asset-data",
                    dttm
                );
                return { rootGeoJSON: null, clientAssetData: legacyData };
            } catch (err) {
                console.error("Failed to load asset data dataset:", err);
                return { rootGeoJSON: null, clientAssetData: null };
            }
        };

        reloadSensorPlacementData()
            .then((placementData) => loadSensorPlacementInputData(placementData))
            .then((inputData) => {
                return loadAssetData(inputData);
            })
            .then((loadedAssetData) => {
                console.log("loaded asset data:", loadedAssetData);
                setRootGeoJSON(loadedAssetData.rootGeoJSON);
                setClientAssetData(loadedAssetData.clientAssetData);
            })
            .catch((err) => {
                console.error("Error loading data:", err);
                setRootGeoJSON(null);
                setClientAssetData(null);
                setSensorPlacementData(null);
                setSensorPlacementInputData(null);
                setCoverageMetrics([]);
                setWindSpeedMetrics([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [client, project]);

    useEffect(() => {
        if (expandedSensorName) sidebarRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, [expandedSensorName]);

    const buildAcceptSensorPayload = useCallback(() => {
        if (!expandedSensorName) return null;
        const installHagMeters = pendingInstallHag !== "" ? (hagUnit === "ft" ? parseFloat(pendingInstallHag) / FEET_PER_METER : parseFloat(pendingInstallHag)) : null;
        const payload = {
            "newly_accepted_sensors": {
                [expandedSensorName]: {
                    install_option_idx: pendingInstallOptionIdx,
                    install_hag: installHagMeters,
                    tower_type: pendingTowerType,
                    allowed_mast_headings: Array.from(pendingAllowedMastHeadings),
                    mounting_tower_leg: TOWER_TYPES_WITHOUT_LEGS.includes(pendingTowerType) ? null : pendingMountingTowerLeg,
                    hilti_install_override: TOWER_TYPES_WITHOUT_LEGS.includes(pendingTowerType) ? (pendingHiltiInstallOverride ?? false) : false,
                }
            }
        };
        return payload;
    }, [expandedSensorName, pendingStructureName, pendingInstallOptionIdx, pendingInstallHag, hagUnit, pendingTowerType, pendingAllowedMastHeadings, pendingMountingTowerLeg, pendingHiltiInstallOverride]);

    const handleSubmitRefinement = useCallback(
        (updates) => {
            const sensorToRefocus = expandedSensorName;
            setLoading(true);
            calibrationService
                .submitSensorPlacementRefinement(
                    client,
                    project,
                    sensorPlacementData?.meta?.version_dttm,
                    sensorPlacementInputData?.meta?.version_dttm,
                    updates
                )
                .then(() => reloadSensorPlacementData())
                .then((placementData) => {
                    if (!sensorToRefocus || !placementData) return;
                    const newStructureName = placementData.data?.sensor_name_structure?.[sensorToRefocus];
                    const newPlacement = newStructureName
                        ? placementData.data?.structure_configs?.[newStructureName]
                        : null;
                    if (!newPlacement) return;
                    const newUserConfig = newPlacement.user_config;
                    setPendingInstallOptionIdx(newUserConfig.install_option_idx);
                    const hagInMeters = parseFloat(newUserConfig.install_hag);
                    setPendingInstallHag(
                        String((hagUnit === "ft" ? hagInMeters * FEET_PER_METER : hagInMeters).toFixed(1))
                    );
                    setPendingTowerType(newUserConfig.tower_type ?? null);
                    setPendingMountingTowerLeg(newUserConfig.mounting_tower_leg ?? null);
                    setPendingHiltiInstallOverride(newUserConfig.hilti_install_override ?? false);
                    setPendingStructureName(newPlacement.structure_name);
                    setChangingStructure(false);
                    const reloadedAllowed = normalizeCardinalDirections(newUserConfig.allowed_mast_headings);
                    const reloadedSpanHeading = newPlacement.sorted_install_options[newUserConfig.install_option_idx]?.span_heading;
                    setPendingAllowedMastHeadings(
                        reloadedAllowed.length > 0
                            ? new Set(reloadedAllowed)
                            : reloadedSpanHeading != null
                                ? defaultAllowedMastHeadings(reloadedSpanHeading)
                                : new Set()
                    );
                    setExpandedSensorName(sensorToRefocus);
                    mapRef.current?.flyTo({
                        latitude: newPlacement.structure_latitude,
                        longitude: newPlacement.structure_longitude,
                    });
                })
                .finally(() => setLoading(false));
        },
        [client, project, sensorPlacementData, sensorPlacementInputData, reloadSensorPlacementData, expandedSensorName, hagUnit, mapRef]
    );

    const structures = useMemo(
        () => normalizeRootGeoJSON(rootGeoJSON)?.structures ?? normalizeClientAssetData(clientAssetData)?.structures ?? [],
        [rootGeoJSON, clientAssetData]
    );
    const ineligibleStructureFromDataset = sensorPlacementData?.data?.ineligible_structure_w_reason ?? {};

    const pdMetrics = sensorPlacementData?.data?.metrics ?? {};
    const uncoveredSpanIds = new Set(pdMetrics.uncovered_span_ids ?? []);
    const uncoverableLineIds = new Set(pdMetrics.uncoverable_lines ?? []);
    const uncoveredLineIds = new Set((pdMetrics.uncovered_line_sets ?? []).flat());
    const hasUncoverableData = uncoverableLineIds.size > 0;
    const hasUncoveredCoverableData = uncoveredLineIds.size > 0;
    const hasSpanData = uncoveredSpanIds.size > 0;

    const sensorNameStatus = sensorPlacementData?.data?.sensor_name_status ?? {};

    const sensorNameToStructure = sensorPlacementData?.data?.sensor_name_structure ?? {};
    const structureConfigs = sensorPlacementData?.data?.structure_configs ?? {};
    const structureToCellComms = sensorPlacementData?.data?.structure_to_cell_comms ?? {};
    const requiredStructures = new Set(sensorPlacementData?.data?.required_structures ?? []);
    const sensorPlacements = Object.entries(sensorNameToStructure).map(([sensorName, structureName]) => ({
        ...(structureConfigs[structureName] ?? {}),
        sensor_name: sensorName,
        sensor_color: sensorNameStatus[sensorName] === "ACCEPTED" ? SENSOR_COLOR_ACCEPTED : SENSOR_COLOR_DEFAULT,
    }));

    const windSpeedsByStructure = sensorPlacementInputData?.data?.adjacent_span_wind_speeds_by_structure ?? {};
    const hasWspdData = Object.keys(windSpeedsByStructure).length > 0;
    const hasCoverageWarning = coverageMetrics.some((metric) => {
        if (metric.valueColor === "#c62828") return true;
        if (metric.subRows) return metric.subRows.some((sub) =>
            sub.valueColor === "#c62828" || (sub.badge && sub.badge.rating !== "Good")
        );
        return false;
    });

    const handleHagUnitToggle = useCallback((e) => {
        e.stopPropagation();
        setHagUnit((prev) => {
            const newUnit = prev === "m" ? "ft" : "m";
            setPendingInstallHag((val) => {
                const num = parseFloat(val);
                if (isNaN(num)) return val;
                const converted = prev === "m" ? num * FEET_PER_METER : num / FEET_PER_METER;
                return String(converted.toFixed(1));
            });
            return newUnit;
        });
    }, []);

    const handleSensorBoxClick = (sensorName) => {
        if (expandedSensorName === sensorName) {
            setExpandedSensorName(null);
            return;
        }
        const placement = sensorPlacements.find((p) => p.sensor_name === sensorName);
        if (placement) {
            setPendingInstallOptionIdx(placement.user_config.install_option_idx);
            const hagInMeters = parseFloat(placement.user_config.install_hag);
            const displayHag = hagUnit === "ft" ? hagInMeters * FEET_PER_METER : hagInMeters;
            setPendingInstallHag(String(displayHag.toFixed(1)));
            setPendingTowerType(placement.user_config.tower_type ?? null);
            setPendingMountingTowerLeg(placement.user_config.mounting_tower_leg ?? null);
            setPendingHiltiInstallOverride(placement.user_config.hilti_install_override ?? false);
            setPendingStructureName(placement.structure_name);
            setChangingStructure(false);
            const existingAllowed = normalizeCardinalDirections(placement.user_config.allowed_mast_headings);
            const spanHeading = placement.sorted_install_options[placement.user_config.install_option_idx]?.span_heading;
            setPendingAllowedMastHeadings(
                existingAllowed.length > 0
                    ? new Set(existingAllowed)
                    : spanHeading != null
                        ? defaultAllowedMastHeadings(spanHeading)
                        : new Set()
            );
            mapRef.current?.flyTo({
                latitude: placement.structure_latitude,
                longitude: placement.structure_longitude,
            });
        }
        setExpandedSensorName(sensorName);
    };

    return (
        <>
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <b>Loading...</b>
                </div>
            )}
            <div className={classes.contentContainer}>
                <AspenMap
                    mapRef={mapRef}
                    rootGeoJSON={rootGeoJSON}
                    clientAssetData={clientAssetData}
                    structures={structures}
                    ineligibleStructureFromDataset={ineligibleStructureFromDataset}
                    ineligibleStructureFromUser={ineligibleStructureFromUser}
                    setIneligibleStructureFromUser={setIneligibleStructureFromUser}
                    windSpeedsByStructure={windSpeedsByStructure}
                    sensorPlacements={sensorPlacements}
                    expandedSensorName={expandedSensorName}
                    pendingStructureName={pendingStructureName}
                    pendingInstallOptionIdx={pendingInstallOptionIdx}
                    onSensorClick={handleSensorBoxClick}
                    onSubmitRefinement={handleSubmitRefinement}
                    showUncoverableLines={showUncoverableLines}
                    showUncoveredCoverableLines={showUncoveredCoverableLines}
                    showUncoveredSpans={showUncoveredSpans}
                    uncoveredSpanIds={uncoveredSpanIds}
                    uncoverableLineIds={uncoverableLineIds}
                    uncoveredLineIds={uncoveredLineIds}
                    uncoveredLineSets={pdMetrics.uncovered_line_sets ?? []}
                    wspdHighlightView={wspdHighlightView}
                />
                <div style={{ width: "380px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
                    {coverageMetrics.length > 0 && (
                        <div className={classes.metricsPanel}>
                            <div
                                onClick={() => setCoverageExpanded((v) => !v)}
                                style={{
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: coverageExpanded ? "8px" : 0,
                                }}
                            >
                                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                    Coverage
                                    {hasCoverageWarning && <WarningAmberIcon style={{ fontSize: 16, color: "#d32f2f" }} />}
                                </span>
                                <span>{coverageExpanded ? "▲" : "▼"}</span>
                            </div>
                            <Collapse in={coverageExpanded}>
                                {coverageMetrics.map(({ key, label, value, valueColor, tooltip, tooltipHeader, tooltipItems, subRows, infoTooltip, summary }) => (
                                    <div key={key}>
                                        <div className={classes.metricsRow}>
                                            <span className={classes.metricsLabel}>{label}: </span>
                                            {value != null && (
                                                <span
                                                    className={classes.metricsValue}
                                                    style={valueColor ? { color: valueColor } : undefined}
                                                >
                                                    {value}
                                                </span>
                                            )}
                                            {key === "span_coverage" && hasSpanData && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setCoverageView((v) => v === "spans" ? "hide" : "spans")}
                                                    style={{ padding: "1px", marginLeft: "4px", color: coverageView === "spans" ? "#1976d2" : "#999999" }}
                                                >
                                                    {coverageView === "spans" ? <VisibilityIcon style={{ fontSize: "14px" }} /> : <VisibilityOffIcon style={{ fontSize: "14px" }} />}
                                                </IconButton>
                                            )}
                                            {(tooltip || tooltipHeader) && (
                                                <Tooltip
                                                    title={
                                                        tooltipHeader ? (
                                                            <div>
                                                                <div>{tooltipHeader}</div>
                                                                {tooltipItems?.length > 0 && (
                                                                    <ul style={{ margin: "4px 0 0 0", paddingLeft: "16px" }}>
                                                                        {tooltipItems.map((item) => (
                                                                            <li key={item}>{item}</li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            tooltip
                                                        )
                                                    }
                                                    arrow
                                                >
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "14px",
                                                            height: "14px",
                                                            borderRadius: "50%",
                                                            border: "1px solid #999999",
                                                            color: "#999999",
                                                            fontSize: "10px",
                                                            lineHeight: 1,
                                                            marginLeft: "4px",
                                                            cursor: "default",
                                                            verticalAlign: "middle",
                                                        }}
                                                    >
                                                        ?
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {infoTooltip && (
                                                <Tooltip title={infoTooltip} arrow>
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "14px",
                                                            height: "14px",
                                                            borderRadius: "50%",
                                                            border: "1px solid #999999",
                                                            color: "#999999",
                                                            fontSize: "10px",
                                                            lineHeight: 1,
                                                            marginLeft: "4px",
                                                            cursor: "default",
                                                            verticalAlign: "middle",
                                                        }}
                                                    >
                                                        ?
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                        {summary && (
                                            <div className={classes.metricsRow} style={{ paddingLeft: "12px" }}>
                                                <span className={classes.metricsLabel}>{summary}</span>
                                            </div>
                                        )}
                                        {subRows?.map(({ key: subKey, label: subLabel, value: subValue, valueColor: subValueColor, tooltipHeader: subTooltipHeader, tooltipItems: subTooltipItems, badge: subBadge }) => (
                                            <div key={subKey} className={classes.metricsRow} style={{ paddingLeft: "24px" }}>
                                                {subBadge ? (
                                                    <span>
                                                        <span className={classes.metricsValue} style={{ color: WSPD_RATING_COLORS[subBadge.rating] }}>{subBadge.count}/{subBadge.totalCount}</span>
                                                        <span className={classes.metricsLabel}>{" cover "}{subLabel}{" ("}</span>
                                                        <span className={classes.metricsValue} style={{ color: WSPD_RATING_COLORS[subBadge.rating] }}>{Math.round(subBadge.percentage * 100)}%</span>
                                                        <span className={classes.metricsLabel}>{")"}</span>
                                                    </span>
                                                ) : (
                                                    <>
                                                        <span className={classes.metricsLabel}>{subLabel}: </span>
                                                        <span
                                                            className={classes.metricsValue}
                                                            style={subValueColor ? { color: subValueColor } : undefined}
                                                        >
                                                            {subValue}
                                                        </span>
                                                    </>
                                                )}
                                                {subKey === "uncoverable_lines" && hasUncoverableData && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setShowUncoverableLines((v) => !v)}
                                                        style={{ padding: "1px", marginLeft: "4px", color: showUncoverableLines ? "#1976d2" : "#999999" }}
                                                    >
                                                        {showUncoverableLines ? <VisibilityIcon style={{ fontSize: "14px" }} /> : <VisibilityOffIcon style={{ fontSize: "14px" }} />}
                                                    </IconButton>
                                                )}
                                                {subKey === "coverable_lines" && hasUncoveredCoverableData && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setShowUncoveredCoverableLines((v) => !v)}
                                                        style={{ padding: "1px", marginLeft: "4px", color: showUncoveredCoverableLines ? "#1976d2" : "#999999" }}
                                                    >
                                                        {showUncoveredCoverableLines ? <VisibilityIcon style={{ fontSize: "14px" }} /> : <VisibilityOffIcon style={{ fontSize: "14px" }} />}
                                                    </IconButton>
                                                )}
                                                {subBadge && hasWspdData && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setWspdHighlightView((v) => v === subKey ? null : subKey)}
                                                        style={{ padding: "1px", marginLeft: "4px", color: wspdHighlightView === subKey ? "#1976d2" : "#999999" }}
                                                    >
                                                        {wspdHighlightView === subKey ? <VisibilityIcon style={{ fontSize: "14px" }} /> : <VisibilityOffIcon style={{ fontSize: "14px" }} />}
                                                    </IconButton>
                                                )}
                                                {subBadge?.tooltip && (
                                                    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{subBadge.tooltip}</span>} arrow>
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: "14px",
                                                                height: "14px",
                                                                borderRadius: "50%",
                                                                border: "1px solid #999999",
                                                                color: "#999999",
                                                                fontSize: "10px",
                                                                lineHeight: 1,
                                                                marginLeft: "4px",
                                                                cursor: "default",
                                                                verticalAlign: "middle",
                                                            }}
                                                        >
                                                            ?
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {subTooltipHeader && (
                                                    <Tooltip
                                                        title={
                                                            <div>
                                                                <div>{subTooltipHeader}</div>
                                                                {subTooltipItems?.length > 0 && (
                                                                    <ul style={{ margin: "4px 0 0 0", paddingLeft: "16px" }}>
                                                                        {subTooltipItems.map((item) => (
                                                                            <li key={item}>{item}</li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        }
                                                        arrow
                                                    >
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: "14px",
                                                                height: "14px",
                                                                borderRadius: "50%",
                                                                border: "1px solid #999999",
                                                                color: "#999999",
                                                                fontSize: "10px",
                                                                lineHeight: 1,
                                                                marginLeft: "4px",
                                                                cursor: "default",
                                                                verticalAlign: "middle",
                                                            }}
                                                        >
                                                            ?
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </Collapse>
                        </div>
                    )}
                    {windSpeedMetrics.length > 0 && (
                        <div className={classes.metricsPanel}>
                            <div
                                onClick={() => setMetricsExpanded((v) => !v)}
                                style={{
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: metricsExpanded ? "8px" : 0,
                                }}
                            >
                                <span>Wind Speed Metrics</span>
                                <span>{metricsExpanded ? "▲" : "▼"}</span>
                            </div>
                            <Collapse in={metricsExpanded}>
                                {windSpeedMetrics.map(
                                    ({ key, metric_type, label, value, valueColor, rows: tableRows }) => {
                                        if (metric_type === "histogram") {
                                            return (
                                                <div key={key}>
                                                    <Histogram histogram={value} />
                                                </div>
                                            );
                                        }
                                        if (metric_type === "table") {
                                            return (
                                                <table
                                                    key={key}
                                                    style={{
                                                        width: "100%",
                                                        borderCollapse: "collapse",
                                                        fontSize: "13px",
                                                        marginTop: "4px",
                                                    }}
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th
                                                                style={{
                                                                    textAlign: "left",
                                                                    color: "#666666",
                                                                    fontWeight: "normal",
                                                                    padding: "2px 4px",
                                                                }}
                                                            />
                                                            <th
                                                                style={{
                                                                    textAlign: "right",
                                                                    color: "#666666",
                                                                    fontWeight: "normal",
                                                                    padding: "2px 4px",
                                                                }}
                                                            >
                                                                Wspd Median
                                                            </th>
                                                            <th
                                                                style={{
                                                                    textAlign: "right",
                                                                    color: "#666666",
                                                                    fontWeight: "normal",
                                                                    padding: "2px 4px",
                                                                }}
                                                            >
                                                                Wspd Range
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {tableRows.map(({ key: rowKey, label: rowLabel, median, range }) => (
                                                            <tr key={rowKey}>
                                                                <td
                                                                    style={{ color: "#666666", padding: "2px 4px" }}
                                                                >
                                                                    {rowLabel}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        fontWeight: "bold",
                                                                        textAlign: "right",
                                                                        padding: "2px 4px",
                                                                    }}
                                                                >
                                                                    {median ?? "—"}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        fontWeight: "bold",
                                                                        textAlign: "right",
                                                                        padding: "2px 4px",
                                                                    }}
                                                                >
                                                                    {range ?? "—"}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            );
                                        }
                                        return (
                                            <div key={key} className={classes.metricsRow}>
                                                <span className={classes.metricsLabel}>{label}: </span>
                                                <span
                                                    className={classes.metricsValue}
                                                    style={valueColor ? { color: valueColor } : undefined}
                                                >
                                                    {value}
                                                </span>
                                            </div>
                                        );
                                    }
                                )}
                            </Collapse>
                        </div>
                    )}
                    {sensorPlacements.length > 0 && (
                        <div ref={sidebarRef} className={classes.sidebar}>
                            {!expandedSensorName ? (
                                <div className={classes.noSensorSelected}>
                                    Click on a sensor to edit
                                </div>
                            ) : (
                                (() => {
                                    const placement = sensorPlacements.find(
                                        (p) => p.sensor_name === expandedSensorName
                                    );
                                    if (!placement) return null;
                                    const committedInstall =
                                        placement.sorted_install_options[placement.user_config.install_option_idx];
                                    const hagInMeters =
                                        pendingInstallHag !== ""
                                            ? hagUnit === "ft"
                                                ? parseFloat(pendingInstallHag) / FEET_PER_METER
                                                : Number(pendingInstallHag)
                                            : null;
                                    const isHagExceedingMax =
                                        committedInstall?.max_safe_install_hag != null &&
                                        hagInMeters !== null &&
                                        parseFloat(hagInMeters.toFixed(1)) >
                                        parseFloat(committedInstall.max_safe_install_hag.toFixed(1));
                                    const sensorColor = placement.sensor_color;
                                    const status = sensorNameStatus[placement.sensor_name];
                                    return (
                                        <div
                                            className={classes.sensorBoxExpanded}
                                            style={{ borderColor: sensorColor, borderWidth: "2px" }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <div className={classes.sensorName}>
                                                    Sensor {placement.sensor_name} - Structure {placement.structure_name}
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                    {requiredStructures.has(placement.structure_name) && (
                                                        <span
                                                            style={{
                                                                fontSize: "10px",
                                                                padding: "1px 5px",
                                                                borderRadius: "4px",
                                                                backgroundColor: "#ede7f6",
                                                                color: "#5e35b1",
                                                                border: "1px solid #5e35b1",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            REQUIRED
                                                        </span>
                                                    )}
                                                    {status && (
                                                        <span
                                                            style={{
                                                                fontSize: "10px",
                                                                padding: "1px 5px",
                                                                borderRadius: "4px",
                                                                backgroundColor:
                                                                    status === "NO_DECISION"
                                                                        ? "#fff3e0"
                                                                        : status === "REJECTED"
                                                                            ? "#ffebee"
                                                                            : "#e8f5e9",
                                                                color:
                                                                    status === "NO_DECISION"
                                                                        ? "#e65100"
                                                                        : status === "REJECTED"
                                                                            ? "#c62828"
                                                                            : "#2e7d32",
                                                                border: `1px solid ${status === "NO_DECISION"
                                                                    ? "#e65100"
                                                                    : status === "REJECTED"
                                                                        ? "#c62828"
                                                                        : "#2e7d32"
                                                                    }`,
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {status.replace(/_/g, " ")}
                                                        </span>
                                                    )}
                                                    <Tooltip title="Zoom to sensor on map">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                mapRef.current?.flyTo({
                                                                    latitude: placement.structure_latitude,
                                                                    longitude: placement.structure_longitude,
                                                                    zoom: 19,
                                                                })
                                                            }
                                                            style={{ padding: "2px" }}
                                                        >
                                                            <MyLocationIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setExpandedSensorName(null)}
                                                        style={{ padding: "2px" }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </div>
                                            </div>
                                            <a
                                                href={`https://www.google.com/maps?q=${placement.structure_latitude},${placement.structure_longitude}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    fontSize: "11px",
                                                    marginTop: "2px",
                                                    display: "block",
                                                    textAlign: "right",
                                                }}
                                            >
                                                View on Google Maps
                                            </a>
                                            {changingStructure ? (
                                                <>
                                                    <Autocomplete
                                                        options={structures.map((s) => s.structure_name)}
                                                        value={pendingStructureName}
                                                        onChange={(e, value) => {
                                                            e.stopPropagation();
                                                            setPendingStructureName(
                                                                value ?? placement.structure_name
                                                            );
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Structure Override"
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        )}
                                                        style={{ marginTop: "12px" }}
                                                        size="small"
                                                    />
                                                    {(() => {
                                                        const s = structures.find(
                                                            (s) => s.structure_name === pendingStructureName
                                                        );
                                                        return s ? (
                                                            <a
                                                                href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{
                                                                    fontSize: "11px",
                                                                    marginTop: "4px",
                                                                    display: "block",
                                                                    textAlign: "right",
                                                                }}
                                                            >
                                                                View on Google Maps
                                                            </a>
                                                        ) : null;
                                                    })()}
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth
                                                        color="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSubmitRefinement({
                                                                swap_sensors: {
                                                                    [placement.sensor_name]: pendingStructureName,
                                                                },
                                                            });
                                                            setChangingStructure(false);
                                                        }}
                                                        style={{ marginTop: "8px" }}
                                                    >
                                                        Save Override
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPendingStructureName(placement.structure_name);
                                                            setChangingStructure(false);
                                                        }}
                                                        style={{ marginTop: "8px" }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    {!ineligibleStructureFromUser.has(placement.structure_name) && (
                                                        <>
                                                            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                                                <TextField
                                                                    type="number"
                                                                    fullWidth
                                                                    label={
                                                                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                                            Install HAG
                                                                            {isHagExceedingMax && (
                                                                                <Tooltip title="Install HAG exceeds estimated maximum safe HAG">
                                                                                    <WarningAmberIcon style={{ fontSize: 16, color: "#d32f2f" }} />
                                                                                </Tooltip>
                                                                            )}
                                                                        </span>
                                                                    }
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={pendingInstallHag}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) =>
                                                                        setPendingInstallHag(e.target.value)
                                                                    }
                                                                    InputProps={{
                                                                        endAdornment: (
                                                                            <InputAdornment position="end">
                                                                                <ToggleButtonGroup
                                                                                    value={hagUnit}
                                                                                    exclusive
                                                                                    onChange={(e, val) => {
                                                                                        if (val && val !== hagUnit) handleHagUnitToggle(e);
                                                                                    }}
                                                                                    size="small"
                                                                                    style={{ height: "26px" }}
                                                                                >
                                                                                    <ToggleButton value="m" style={{ padding: "2px 7px", fontSize: "11px" }} sx={{ "&.Mui-selected": { backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } } }}>m</ToggleButton>
                                                                                    <ToggleButton value="ft" style={{ padding: "2px 7px", fontSize: "11px" }} sx={{ "&.Mui-selected": { backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } } }}>ft</ToggleButton>
                                                                                </ToggleButtonGroup>
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                                <TextField
                                                                    select
                                                                    fullWidth
                                                                    label="Span"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={pendingInstallOptionIdx}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) =>
                                                                        setPendingInstallOptionIdx(
                                                                            Number(e.target.value)
                                                                        )
                                                                    }
                                                                >
                                                                    {placement.sorted_install_options.map(
                                                                        (option, idx) => (
                                                                            <MenuItem key={idx} value={idx}>
                                                                                {option.span_id}
                                                                            </MenuItem>
                                                                        )
                                                                    )}
                                                                </TextField>
                                                            </div>
                                                            {committedInstall && (
                                                                <div
                                                                    style={{
                                                                        fontSize: "11px",
                                                                        color: "#666666",
                                                                        marginTop: "4px",
                                                                    }}
                                                                >
                                                                    Estimated max safe install HAG:{" "}
                                                                    {committedInstall.max_safe_install_hag != null
                                                                        ? hagUnit === "ft"
                                                                            ? (
                                                                                committedInstall.max_safe_install_hag *
                                                                                FEET_PER_METER
                                                                            ).toFixed(1)
                                                                            : committedInstall.max_safe_install_hag.toFixed(
                                                                                1
                                                                            )
                                                                        : null}
                                                                    {hagUnit}
                                                                </div>
                                                            )}
                                                            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                                                <TextField
                                                                    select
                                                                    style={{ flex: 1 }}
                                                                    label="Tower Type"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={pendingTowerType ?? ""}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) =>
                                                                        setPendingTowerType(e.target.value || null)
                                                                    }
                                                                >
                                                                    <MenuItem value="">Unknown</MenuItem>
                                                                    {TOWER_TYPES.map((value) => (
                                                                        <MenuItem key={value} value={value}>{value}</MenuItem>
                                                                    ))}
                                                                </TextField>
                                                                <Tooltip
                                                                    title={TOWER_TYPES_WITHOUT_LEGS.includes(pendingTowerType) ? "H-Frame, Wood Monopole, and Steel Monopole tower types do not require a mounting leg" : ""}
                                                                    arrow
                                                                >
                                                                    <span style={{ flex: 1, display: "block" }}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Mounting Tower Leg"
                                                                            variant="outlined"
                                                                            size="small"
                                                                            value={TOWER_TYPES_WITHOUT_LEGS.includes(pendingTowerType) ? "N/A" : (pendingMountingTowerLeg ?? "")}
                                                                            disabled={TOWER_TYPES_WITHOUT_LEGS.includes(pendingTowerType)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            onChange={(e) => setPendingMountingTowerLeg(e.target.value || null)}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            </div>
                                                            {(() => {
                                                                const currentOption = placement.sorted_install_options[pendingInstallOptionIdx];
                                                                const keyDirs = currentOption ? getSpanKeyDirections(currentOption.span_heading) : new Set();
                                                                return (
                                                                    <div style={{ marginTop: "8px" }}>
                                                                        <div style={{ fontSize: "12px", color: "#666666", marginBottom: "3px", display: "flex", alignItems: "center", gap: "4px" }}>
                                                                            Allowed Mast Headings
                                                                            <Tooltip
                                                                                title="The mast on which the anemometer is mounted should ideally be parallel or perpendicular to the span the camera is monitoring. Select the headings which are possible for install"
                                                                                arrow
                                                                            >
                                                                                <span
                                                                                    style={{
                                                                                        display: "inline-flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        width: "14px",
                                                                                        height: "14px",
                                                                                        borderRadius: "50%",
                                                                                        border: "1px solid #999999",
                                                                                        color: "#999999",
                                                                                        fontSize: "10px",
                                                                                        lineHeight: 1,
                                                                                        cursor: "default",
                                                                                        flexShrink: 0,
                                                                                    }}
                                                                                >
                                                                                    ?
                                                                                </span>
                                                                            </Tooltip>
                                                                        </div>
                                                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 26px)", gap: "2px" }}>
                                                                            {COMPASS_GRID.flatMap((row) =>
                                                                                row.map((dir) => {
                                                                                    if (dir === null) {
                                                                                        const hRad = currentOption ? (currentOption.span_heading * Math.PI) / 180 : 0;
                                                                                        const L = 11;
                                                                                        const cx = 13, cy = 13;
                                                                                        const tooltipTitle = currentOption ? `${currentOption.span_heading.toFixed(1)}°` : "";
                                                                                        return (
                                                                                            <Tooltip key="center" title={tooltipTitle} arrow>
                                                                                                <div style={{ width: "26px", height: "26px" }}>
                                                                                                    <svg width="26" height="26" viewBox="0 0 26 26">
                                                                                                        <line
                                                                                                            x1={cx + Math.sin(hRad) * L}
                                                                                                            y1={cy - Math.cos(hRad) * L}
                                                                                                            x2={cx - Math.sin(hRad) * L}
                                                                                                            y2={cy + Math.cos(hRad) * L}
                                                                                                            stroke="#666666"
                                                                                                            strokeWidth="2"
                                                                                                            strokeLinecap="round"
                                                                                                        />
                                                                                                    </svg>
                                                                                                </div>
                                                                                            </Tooltip>
                                                                                        );
                                                                                    }
                                                                                    const isAllowed = pendingAllowedMastHeadings.has(dir);
                                                                                    const isKeyDir = keyDirs.has(dir);
                                                                                    return (
                                                                                        <button
                                                                                            key={dir}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setPendingAllowedMastHeadings((prev) => {
                                                                                                    const next = new Set(prev);
                                                                                                    if (next.has(dir)) next.delete(dir);
                                                                                                    else next.add(dir);
                                                                                                    return next;
                                                                                                });
                                                                                            }}
                                                                                            style={{
                                                                                                width: "26px",
                                                                                                height: "26px",
                                                                                                border: isAllowed && isKeyDir ? "1px solid #1976d2" : "1px solid #cccccc",
                                                                                                borderRadius: "3px",
                                                                                                backgroundColor: !isAllowed ? "#f5f5f5" : isKeyDir ? "#1976d2" : "#e0e0e0",
                                                                                                color: !isAllowed ? "#aaaaaa" : isKeyDir ? "#ffffff" : "#555555",
                                                                                                fontSize: "10px",
                                                                                                fontWeight: "bold",
                                                                                                cursor: "pointer",
                                                                                                padding: 0,
                                                                                                display: "flex",
                                                                                                alignItems: "center",
                                                                                                justifyContent: "center",
                                                                                                lineHeight: 1,
                                                                                            }}
                                                                                        >
                                                                                            {!isAllowed ? "×" : dir}
                                                                                        </button>
                                                                                    );
                                                                                })
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                            {TOWER_TYPES_WITHOUT_LEGS.includes(pendingTowerType) && (
                                                                <div style={{ marginTop: "8px" }}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={pendingHiltiInstallOverride ?? false}
                                                                                onChange={(e) => setPendingHiltiInstallOverride(e.target.checked)}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label={
                                                                            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#666" }}>
                                                                                Hilti Install Override
                                                                                <Tooltip title="This override is needed when corten towers are identified after scoping in order to reflect hardware requirement changes in the pick list" arrow>
                                                                                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", border: "1px solid #999", color: "#999", fontSize: 10, lineHeight: 1, cursor: "default", flexShrink: 0 }}>?</span>
                                                                                </Tooltip>
                                                                            </span>
                                                                        }
                                                                    />
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {status !== "ACCEPTED" && (
                                                        <>
                                                            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    color={
                                                                        ineligibleStructureFromUser.has(
                                                                            placement.structure_name
                                                                        )
                                                                            ? "inherit"
                                                                            : "error"
                                                                    }
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSubmitRefinement({
                                                                            newly_reviewed_inaccessible: [
                                                                                placement.structure_name,
                                                                            ],
                                                                        });
                                                                    }}
                                                                >
                                                                    Mark Inaccessible
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    color="success"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (isHagExceedingMax) {
                                                                            setHagWarningDialog(true);
                                                                        } else {
                                                                            handleSubmitRefinement(buildAcceptSensorPayload());
                                                                        }
                                                                    }}
                                                                >
                                                                    Accept Structure
                                                                </Button>
                                                            </div>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                fullWidth
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setChangingStructure(true);
                                                                }}
                                                                style={{ marginTop: "8px" }}
                                                            >
                                                                Manual Structure Selection
                                                            </Button>
                                                        </>
                                                    )}
                                                    {status === "ACCEPTED" && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isHagExceedingMax) {
                                                                    setHagWarningDialog(true);
                                                                } else {
                                                                    handleSubmitRefinement(buildAcceptSensorPayload());
                                                                }
                                                            }}
                                                            style={{ marginTop: "12px" }}
                                                        >
                                                            Update Structure Config
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                            {(() => {
                                                const cellComms = structureToCellComms[placement.structure_name];
                                                if (!cellComms) return null;
                                                const rows = cellComms.region === "UK"
                                                    ? [
                                                        { label: "UK Cell Provider", value: cellComms.uk_cell_provider },
                                                        { label: "Nearest Cell Tower Distance (km)", value: cellComms.uk_nearest_cell_tower_distance_km != null ? Number(cellComms.uk_nearest_cell_tower_distance_km).toFixed(2) : null, color: cellComms.uk_nearest_cell_tower_distance_km != null ? (cellComms.uk_nearest_cell_tower_distance_km < 10 ? "#2e7d32" : null) : null },
                                                    ]
                                                    : cellComms.region === "US"
                                                        ? [
                                                            { label: "RSSI T-Mobile (dBm)", value: cellComms.rssi_tmobile, color: getCellCommsColor(cellComms.rssi_tmobile) },
                                                            { label: "RSSI AT&T (dBm)", value: cellComms.rssi_att, color: getCellCommsColor(cellComms.rssi_att) },
                                                        ]
                                                        : [];
                                                if (!rows.length) return null;
                                                return (
                                                    <div style={{ marginTop: "10px" }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                                                            <span style={{ fontSize: "10px", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>Cell Comms Availability</span>
                                                            <div style={{ flex: 1, height: 1, backgroundColor: "#c8d4e8" }} />
                                                        </div>
                                                        <div style={{ fontSize: "12px" }}>
                                                            {rows.map(({ label, value, color }) => (
                                                                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", borderBottom: "1px solid #e0e0e0" }}>
                                                                    <span style={{ color: "#666666" }}>{label}</span>
                                                                    <span style={{ fontWeight: "bold", color: color ?? (value != null ? "inherit" : "#bbbbbb") }}>{value != null ? String(value) : "N/A"}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    )}
                    {sensorPlacements.length > 0 &&
                        (() => {
                            const allAccepted = sensorPlacements.every(
                                (p) => sensorNameStatus[p.sensor_name] === "ACCEPTED"
                            );
                            const allConfigsComplete = sensorPlacements.every((p) => {
                                const config = p.user_config;
                                if (!config.tower_type) return false;
                                if (!TOWER_TYPES_WITHOUT_LEGS.includes(config.tower_type) && !config.mounting_tower_leg) return false;
                                if (TOWER_TYPES_WITHOUT_LEGS.includes(config.tower_type) && config.hilti_install_override == null) return false;
                                if (!config.allowed_mast_headings?.length) return false;
                                return true;
                            });
                            const projectDetails = sensorPlacementData?.data?.project_installation_details;
                            const projectConfigured = projectDetails != null &&
                                ["pv_size", "pv_angle", "call_schedule", "deliverable"].every(
                                    (f) => projectDetails[f] !== null && projectDetails[f] !== undefined
                                );
                            const saveTooltip = !allAccepted
                                ? "All sensors must be accepted before saving"
                                : !allConfigsComplete
                                    ? "All sensors must have tower type, allowed mast headings, and (for lattice towers) mounting leg filled in"
                                    : !projectConfigured
                                        ? "Project installation details must be complete before saving"
                                        : "";
                            return (
                                <Tooltip title={saveTooltip}>
                                    <span>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            disabled={loading || !allAccepted || !allConfigsComplete || !projectConfigured}
                                            onClick={async () => {
                                                setLoading(true);
                                                try {
                                                    await calibrationService.createSensorFieldPlan(
                                                        client,
                                                        project,
                                                        sensorPlacementData?.meta?.version_dttm
                                                    );
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                        >
                                            Save Field Plan
                                        </Button>
                                    </span>
                                </Tooltip>
                            );
                        })()}
                </div>
            </div>
            <Dialog open={hagWarningDialog} onClose={() => setHagWarningDialog(false)}>
                <DialogTitle>Install HAG exceeds estimated maximum</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The install HAG you entered exceeds the estimated maximum safe install HAG. Are you sure you
                        want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHagWarningDialog(false)}>Cancel</Button>
                    <Button
                        color="warning"
                        onClick={() => {
                            handleSubmitRefinement(buildAcceptSensorPayload());
                            setHagWarningDialog(false);
                        }}
                    >
                        Proceed
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
